import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, X, Brain } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useGuidedStudyByPassageData } from '@/data/study-guide/guided-study-by-passage-data-context';
import { PassageDataContext } from '@/data/passage/passage-data-context';
import { use, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, addDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import topoBackground from '../../../assets/expedition-topo-bg.png';
import { useAdminAuth } from '@/lib/hooks/use-admin-auth';

interface GuidedStudyEmptyStateProps {
    isDarkMode: boolean;
    passageId: string;
    onClose: () => void;
}

export function GuidedStudyEmptyState({
    isDarkMode,
    passageId,
    onClose,
}: GuidedStudyEmptyStateProps) {
    const { isAdmin } = useAdminAuth();
    const [isGenerating, setIsGenerating] = useState(false);
    const [isRequesting, setIsRequesting] = useState(false);
    const { loadGuidedStudyByPassageId } = useGuidedStudyByPassageData();
    const { get: getPassage } = use(PassageDataContext);

    // Handler for generating guided study (admin only)
    const handleGenerateGuidedStudy = async () => {
        if (!confirm('Generate Guided Study with Gemini 3.1 Pro? This may take a few minutes.')) {
            return;
        }

        setIsGenerating(true);
        const functions = getFunctions();
        const generateFunction = httpsCallable(functions, 'generateGuidedStudyByPassage', {
            timeout: 300000 // 5 minutes
        });

        try {
            console.log(`Generating guided study for passage ${passageId}...`);
            await generateFunction({ passageId, model: 'gemini-3.1-pro-preview' });
            console.log(`Successfully generated guided study for passage ${passageId}`);
            // Reload the guided study data
            await loadGuidedStudyByPassageId(passageId);
            alert('Guided study generated successfully!');
        } catch (error) {
            console.error(`Failed to generate guided study for passage ${passageId}:`, error);
            alert('Failed to generate guided study. Check console for details.');
        } finally {
            setIsGenerating(false);
        }
    };

    // Handler for requesting a study (user)
    const handleRequestStudy = async () => {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
            alert('Please sign in to request a study.');
            return;
        }

        const storageKey = `guided_study_req_${passageId}_${user.uid}`;
        if (localStorage.getItem(storageKey)) {
            alert('You have already requested a study for this passage. We are working on it!');
            return;
        }

        setIsRequesting(true);
        const db = getFirestore();
        const feedbackRef = collection(db, 'feedback');

        try {
            const passage = getPassage(passageId);
            const reference = passage ? `${passage.book} ${passage.ref}` : passageId;

            await addDoc(feedbackRef, {
                type: 'study_request',
                content: `User requested a guided study for ${reference} (ID: ${passageId})`,
                passageId,
                userId: user.uid,
                timestamp: new Date(),
                url: window.location.href,
                userAgent: navigator.userAgent,
                contextType: 'guided-study'
            });

            localStorage.setItem(storageKey, 'true');
            alert('Thank you! Your request has been submitted.');
        } catch (error) {
            console.error('Failed to submit request:', error);
            alert('Failed to submit request. Please try again.');
        } finally {
            setIsRequesting(false);
        }
    };

    return (
        <div className={cn(
            "relative w-full overflow-hidden min-h-[320px] transition-all duration-500",
            isDarkMode ? "bg-[#0a0a0b] text-slate-200" : "bg-[#fdfbf7] text-slate-800"
        )}>
            {/* Expedition Background Visual */}
            <div className={`absolute inset-0 z-0 pointer-events-none overflow-hidden ${isDarkMode ? 'opacity-40' : 'opacity-40'}`}>
                <img
                    src={topoBackground}
                    alt=""
                    className={`w-full h-full object-cover scale-110 transition-opacity duration-1000 ${isDarkMode ? 'brightness-[1.8] saturate-[1.2] mix-blend-screen' : 'brightness-95 saturate-[0.8] mix-blend-multiply'} `}
                />
                <div className={`absolute inset-0 ${isDarkMode
                    ? 'bg-gradient-to-b from-[#0a0a0b] via-transparent to-[#0a0a0b]'
                    : 'bg-gradient-to-b from-[#fdfbf7] via-transparent to-[#fdfbf7]'
                    } `}
                />
                {/* Subtle Map Texture Overlay */}
                <div className={`absolute inset-0 pointer-events-none ${isDarkMode ? 'opacity-[0.03] invert' : 'opacity-[0.03]'}`} style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/parchment.png")' }} />
            </div>

            <div className="container mx-auto px-4 py-10 max-w-4xl relative z-10">
                <div className="flex justify-between items-start mb-6">
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border-primary/20 bg-primary/5 text-primary">
                                Coming Soon
                            </Badge>
                        </div>
                        <h2 className="text-3xl font-bold tracking-tight">No Guided Study Yet</h2>
                        <p className="text-muted-foreground text-lg max-w-2xl leading-relaxed">
                            We haven't developed a guided journey for this passage yet. Each study is curated to help you engage deeply.
                        </p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full hover:bg-primary/10 transition-colors">
                        <X className="h-5 w-5" />
                    </Button>
                </div>

                <div className="flex flex-wrap gap-4 pt-6">
                    <Button
                        variant="default"
                        className="gap-2 h-11 px-6 font-semibold shadow-lg shadow-primary/20"
                        onClick={handleRequestStudy}
                        disabled={isRequesting || isGenerating}
                    >
                        {isRequesting ? (
                            <>
                                <Sparkles className="h-4 w-4 animate-pulse" />
                                Submitting Request...
                            </>
                        ) : (
                            <>
                                <Sparkles className="h-4 w-4" />
                                Request a Study
                            </>
                        )}
                    </Button>

                    {isAdmin && (
                        <Button
                            variant="outline"
                            className="gap-2 h-11 px-6 font-semibold border-2 hover:bg-primary/5 transition-all"
                            onClick={handleGenerateGuidedStudy}
                            disabled={isRequesting || isGenerating}
                        >
                            {isGenerating ? (
                                <>
                                    <Brain className="h-4 w-4 animate-pulse" />
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <Brain className="h-4 w-4" />
                                    Generate Guided Study
                                </>
                            )}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
